import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, ToggleLeft, ToggleRight, KeyRound, Users } from "lucide-react";

export default function AdminCashiers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [editingPw, setEditingPw] = useState({});

  const load = () =>
    api
      .get("/admin/cashiers")
      .then((r) => {
        setItems(r.data.items || []);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Couldn't load cashiers");
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const createCashier = async () => {
    if (!form.name || !form.email || form.password.length < 6) {
      return toast.error("Name, email, and password (≥6 chars) are required");
    }
    try {
      await api.post("/admin/cashiers", form);
      toast.success(`Cashier ${form.email} created`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create cashier");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/cashiers/${c.id}`, { is_active: !c.is_active });
      load();
    } catch {
      toast.error("Couldn't update cashier");
    }
  };

  const resetPassword = async (c) => {
    const newPw = editingPw[c.id];
    if (!newPw || newPw.length < 6) return toast.error("Enter a password (≥6 chars)");
    try {
      await api.patch(`/admin/cashiers/${c.id}`, { password: newPw });
      toast.success("Password reset");
      setEditingPw({ ...editingPw, [c.id]: "" });
    } catch {
      toast.error("Couldn't reset password");
    }
  };

  const deleteCashier = async (c) => {
    if (!window.confirm(`Delete cashier ${c.email}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/admin/cashiers/${c.id}`);
      toast.success("Cashier deleted");
      load();
    } catch {
      toast.error("Couldn't delete cashier");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-cashiers-page">
      <div>
        <div className="ipv-eyebrow flex items-center gap-2">
          <Users className="w-3 h-3" /> Counter staff
        </div>
        <h1 className="font-display font-black text-3xl">Cashiers</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          Cashier accounts can ring up walk-in sales at <code>/pos</code> but
          cannot edit products, discounts, or settings. Each cashier needs
          their own login.
        </p>
      </div>

      {/* Create form */}
      <div className="border border-gray-200 p-4 bg-gray-50">
        <h3 className="font-bold mb-3 inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Add cashier
        </h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <input
            className="input-base"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="cashier-form-name"
          />
          <input
            className="input-base"
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="cashier-form-email"
          />
          <input
            className="input-base"
            placeholder="Password (≥6 chars)"
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="cashier-form-password"
          />
          <button
            onClick={createCashier}
            className="bg-[#0A1C40] text-white font-bold py-2 rounded-full hover:bg-black transition-colors"
            data-testid="cashier-form-submit"
          >
            Create
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-300">
          No cashiers yet. Create one above so your counter staff can sign in
          at <code>/pos</code>.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-2">Name</th>
                <th className="p-2">Email</th>
                <th className="p-2">Status</th>
                <th className="p-2">Reset password</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`cashier-row-${c.id}`}>
                  <td className="p-2 font-bold">{c.name}</td>
                  <td className="p-2 font-mono text-xs">{c.email}</td>
                  <td className="p-2">
                    <button
                      onClick={() => toggleActive(c)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                        c.is_active === false
                          ? "bg-gray-200 text-gray-600"
                          : "bg-green-100 text-green-800"
                      }`}
                      data-testid={`cashier-toggle-${c.id}`}
                    >
                      {c.is_active === false ? (
                        <>
                          <ToggleLeft className="w-3 h-3" /> Inactive
                        </>
                      ) : (
                        <>
                          <ToggleRight className="w-3 h-3" /> Active
                        </>
                      )}
                    </button>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      <input
                        type="text"
                        className="input-base text-xs"
                        placeholder="New password"
                        value={editingPw[c.id] || ""}
                        onChange={(e) =>
                          setEditingPw({ ...editingPw, [c.id]: e.target.value })
                        }
                      />
                      <button
                        onClick={() => resetPassword(c)}
                        className="px-2 bg-[#FFC220] text-[#0A1C40] text-xs font-bold rounded hover:bg-[#FFB300]"
                        data-testid={`cashier-reset-pw-${c.id}`}
                      >
                        <KeyRound className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <button
                      onClick={() => deleteCashier(c)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                      data-testid={`cashier-delete-${c.id}`}
                      title="Delete cashier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
